import React, { useEffect, useRef, useState } from "react";
import { LocateFixed, MapPin, Loader2, Search, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Map from "@/components/map/MapProvider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * LocationPicker — Phase 3 · Reusable "📍 current location" + "🗺️
 * choose on map" dual-path input for any listing / creation form.
 *
 * GPS-safe (never a prerequisite for saving), worldwide (consumes
 * `/api/geocode/lookup`), respects LOC-1 precedence (an explicit
 * search/pinned location beats device GPS).
 *
 * Props:
 *   value:  { lat, lng, address?, city?, source? } | null
 *   onChange: (loc) => void  — { lat, lng, city, source }
 */
export default function LocationPicker({ value, onChange }) {
  const [mode, setMode] = useState("idle");     // idle | picking-map | resolving
  const [q,    setQ]    = useState("");
  const [hits, setHits] = useState([]);
  const [pending, setPending] = useState(null); // temp {lat,lng} chosen inside picker
  const [gpsBusy, setGpsBusy] = useState(false);
  const qTimer = useRef(null);

  const setLoc = (loc) => onChange?.(loc);

  // 📍 Current-location path — no side-effect on failure other than a
  // toast. NEVER overwrites `value` silently: if GPS fails and the
  // user already had a manual pin, the pin stays.
  const useMyGps = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setGpsBusy(false);
        setLoc({
          lat:    Number(p.coords.latitude.toFixed(6)),
          lng:    Number(p.coords.longitude.toFixed(6)),
          source: "gps",
        });
        toast.success("Location captured from GPS.");
      },
      (e) => {
        setGpsBusy(false);
        toast.error(`${e?.message || "GPS unavailable"} — try 'Choose on map' instead.`);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
    );
  };

  // 🗺️ Choose-on-map path — opens the picker with geocode search.
  const openPicker = () => {
    setPending(value ? { lat: value.lat, lng: value.lng } : null);
    setMode("picking-map");
  };
  const confirmPicker = () => {
    if (!pending) { toast.error("Tap or long-press the map to place a pin first."); return; }
    setLoc({
      lat:    Number(pending.lat.toFixed(6)),
      lng:    Number(pending.lng.toFixed(6)),
      city:   pending.city || value?.city,
      source: pending.source || "map",
    });
    setMode("idle");
  };

  // Geocode lookup (debounced) — LOC-1b endpoint.
  useEffect(() => {
    if (!q || q.length < 2) { setHits([]); return; }
    if (qTimer.current) clearTimeout(qTimer.current);
    qTimer.current = setTimeout(async () => {
      try {
        const r = await api.get("/geocode/lookup", { params: { q } });
        if (r.data?.resolved) setHits([r.data]);
        else                  setHits([]);
      } catch { setHits([]); }
    }, 320);
    return () => qTimer.current && clearTimeout(qTimer.current);
  }, [q]);

  const pickGeocodeHit = (h) => {
    setPending({ lat: h.lat, lng: h.lng, city: h.normalized_city, source: "search" });
    setQ(h.normalized_city || h.text);
    setHits([]);
  };

  // ------------------------------------------------------------------
  return (
    <div className="space-y-2" data-testid="location-picker">
      {/* Dual-path CTAs */}
      <div className="flex gap-2 flex-wrap">
        <Button type="button" onClick={useMyGps} disabled={gpsBusy}
                className="h-9 text-[11px] font-black bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                data-testid="location-picker-gps">
          {gpsBusy ? <Loader2 size={12} className="animate-spin"/> : <LocateFixed size={12}/>}
          Use my current location
        </Button>
        <Button type="button" onClick={openPicker}
                className="h-9 text-[11px] font-black bg-stone-900 hover:bg-black text-white gap-1"
                data-testid="location-picker-map">
          <MapPin size={12}/> Choose on map
        </Button>
      </div>

      {value?.lat != null && (
        <div className="text-[11px] text-stone-600 inline-flex items-center gap-2" data-testid="location-picker-current">
          <MapPin size={11} className="text-emerald-600"/>
          <span>
            {value.city ? <b className="text-stone-800">{value.city}</b> : null}{" "}
            <span className="text-stone-500">
              ({Number(value.lat).toFixed(4)}, {Number(value.lng).toFixed(4)})
            </span>
            {value.source && <span className="ml-1 text-[9px] uppercase tracking-wider text-stone-400">via {value.source}</span>}
          </span>
        </div>
      )}

      {/* Full-screen map picker */}
      {mode === "picking-map" && (
        <div className="fixed inset-0 z-[1200] bg-black/60 flex flex-col" data-testid="location-picker-map-modal">
          <div className="bg-white px-3 py-2 flex items-center gap-2 border-b border-stone-200">
            <button onClick={() => setMode("idle")} className="p-1 text-stone-500 hover:bg-stone-100 rounded-full">
              <X size={16}/>
            </button>
            <div className="text-[13px] font-black text-stone-800">Choose a location</div>
            <div className="relative flex-1 ml-2">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search city, neighbourhood, landmark…"
                     className="pl-8 h-8 text-sm rounded-full"
                     data-testid="location-picker-search"/>
              {hits.length > 0 && (
                <div className="absolute left-0 right-0 top-9 bg-white shadow-lg border border-stone-200 rounded-xl overflow-hidden z-[10]">
                  {hits.map(h => (
                    <button key={`${h.lat},${h.lng}`} onClick={() => pickGeocodeHit(h)}
                            className="w-full text-left px-3 py-2 hover:bg-stone-100 text-[12px]"
                            data-testid="location-picker-hit">
                      <div className="font-bold">{h.normalized_city}</div>
                      <div className="text-[10px] text-stone-500">{h.country} · {h.lat.toFixed(3)}, {h.lng.toFixed(3)}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={confirmPicker} className="h-8 text-[11px] font-black bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                    data-testid="location-picker-confirm">
              <Check size={12}/> Confirm
            </Button>
          </div>

          <div className="flex-1 relative">
            <Map
              provider="maplibre"
              center={pending ? [pending.lat, pending.lng] : (value ? [value.lat, value.lng] : [19.076, 72.8777])}
              zoom={pending || value ? 13 : 4}
              markers={pending ? [{ key: "pin", lat: pending.lat, lng: pending.lng, icon: "📍", color: "#059669" }] : []}
              onMapClick={(c)     => setPending({ ...c, source: "map" })}
              onLongPress={(c)    => setPending({ ...c, source: "map" })}
              height="100%"
            />
            <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[500]">
              <div className="w-6 h-6 rounded-full border-2 border-emerald-600 bg-white/80 shadow flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-600"/>
              </div>
            </div>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/95 shadow px-3 py-1.5 text-[11px] font-bold text-stone-700">
              Tap or long-press the map to place a pin
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
